import { ClientSafeLaunchCertificateViewV1Schema, LaunchCertificateArtifactV1Schema, PublicLaunchCertificateVerificationV1Schema, } from "../contracts/launch-certificate.js";
import { FORBIDDEN_CERTIFICATE_CLAIM_PHRASES, LAUNCH_CERTIFICATE_DISCLAIMERS, LAUNCH_CERTIFICATE_HEADLINE_ISSUED, LAUNCH_CERTIFICATE_HEADLINE_WITH_WARNINGS, LAUNCH_CERTIFICATE_TITLE, LAUNCH_CERTIFICATE_WARNING_BANNER, } from "./launch-certificate-copy.js";
import { LAUNCH_PROOF_CLIENT_SAFE_FORBIDDEN_KEYS } from "../reports/sanitize-client-safe-launch-proof.js";
import { assertNoForbiddenClientSafePhrases } from "../reports/sanitize-client-safe-report.js";
const CERTIFICATE_FORBIDDEN_KEYS = [
    ...LAUNCH_PROOF_CLIENT_SAFE_FORBIDDEN_KEYS,
    "certificateId",
    "verificationCode",
    "runId",
    "agencyId",
    "projectId",
    "artifactJson",
    "clientSafeHtml",
    "internalReportJson",
    "traceId",
    "correlationId",
    "snapshotHash",
    "exportBundleHash",
];
export function assertNoClientSafeLaunchCertificateLeaks(serialized) {
    for (const key of CERTIFICATE_FORBIDDEN_KEYS) {
        if (serialized.includes(`"${key}"`)) {
            throw new Error(`client_safe_launch_certificate_leak:${key}`);
        }
    }
    assertNoForbiddenClientSafePhrases(serialized);
}
export function assertNoUnauthorizedPositiveClaims(input) {
    const positiveText = [
        input.title,
        input.headline,
        input.aggregateStatusLabel,
        input.warningBanner ?? "",
        ...input.evidenceSections.flatMap((section) => [
            section.areaLabel,
            section.statusLabel,
            section.summary,
        ]),
    ]
        .join(" ")
        .toLowerCase();
    for (const phrase of FORBIDDEN_CERTIFICATE_CLAIM_PHRASES) {
        if (positiveText.includes(phrase)) {
            throw new Error(`client_safe_launch_certificate_forbidden_claim:${phrase}`);
        }
    }
}
export function toClientSafeLaunchCertificateView(artifact) {
    const parsed = LaunchCertificateArtifactV1Schema.parse(artifact);
    const view = ClientSafeLaunchCertificateViewV1Schema.parse({
        schemaVersion: "client-safe-launch-certificate-view.v1.0",
        title: LAUNCH_CERTIFICATE_TITLE,
        businessName: parsed.businessName,
        agencyDisplayName: parsed.agencyDisplayName,
        proofReferenceLabel: parsed.proofReferenceLabel,
        aggregateStatusLabel: parsed.aggregateStatusLabel,
        headline: parsed.headline,
        evidenceSections: parsed.evidenceSections,
        warningBanner: parsed.warningBanner,
        issuedAt: parsed.issuedAt,
        disclaimers: parsed.disclaimers,
        clientSafe: true,
    });
    const blob = JSON.stringify(view);
    assertNoUnauthorizedPositiveClaims(view);
    assertNoClientSafeLaunchCertificateLeaks(blob);
    return view;
}
export function assertNoPublicVerificationLeaks(serialized) {
    for (const key of CERTIFICATE_FORBIDDEN_KEYS) {
        if (key === "verificationCode")
            continue;
        if (serialized.includes(`"${key}"`)) {
            throw new Error(`client_safe_launch_certificate_leak:${key}`);
        }
    }
    assertNoForbiddenClientSafePhrases(serialized);
}
export function toPublicVerificationView(artifact) {
    const clientView = toClientSafeLaunchCertificateView(artifact);
    const verification = PublicLaunchCertificateVerificationV1Schema.parse({
        schemaVersion: "public-launch-certificate-verification.v1.0",
        verificationCode: artifact.verificationCode,
        businessName: clientView.businessName,
        agencyDisplayName: clientView.agencyDisplayName,
        proofReferenceLabel: clientView.proofReferenceLabel,
        aggregateStatusLabel: clientView.aggregateStatusLabel,
        headline: clientView.headline,
        evidenceSections: clientView.evidenceSections,
        warningBanner: clientView.warningBanner,
        issuedAt: clientView.issuedAt,
        disclaimers: clientView.disclaimers,
    });
    const blob = JSON.stringify(verification);
    assertNoUnauthorizedPositiveClaims(clientView);
    assertNoPublicVerificationLeaks(blob);
    return verification;
}
export function aggregateStatusLabelForCertificate(status) {
    switch (status) {
        case "ready":
            return "Evaluated checks passed at this reference";
        case "ready_with_warnings":
            return "Completed with warnings";
        default:
            return "Not eligible";
    }
}
export function headlineForCertificate(status) {
    return status === "ready_with_warnings"
        ? LAUNCH_CERTIFICATE_HEADLINE_WITH_WARNINGS
        : LAUNCH_CERTIFICATE_HEADLINE_ISSUED;
}
export function warningBannerForCertificate(status) {
    return status === "ready_with_warnings" ? LAUNCH_CERTIFICATE_WARNING_BANNER : null;
}
export function defaultCertificateDisclaimers() {
    return [...LAUNCH_CERTIFICATE_DISCLAIMERS];
}
export function renderLaunchCertificatePrintableHtml(view) {
    const sectionsHtml = view.evidenceSections
        .map((section) => `<section><h3>${escapeHtml(section.areaLabel)}</h3><p><strong>${escapeHtml(section.statusLabel)}</strong> — ${escapeHtml(section.summary)}</p></section>`)
        .join("\n");
    const disclaimers = view.disclaimers.map((d) => `<li>${escapeHtml(d)}</li>`).join("");
    const warning = view.warningBanner
        ? `<p class="warning">${escapeHtml(view.warningBanner)}</p>`
        : "";
    return `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"/><title>${escapeHtml(view.title)}</title><style>body{font-family:system-ui,sans-serif;max-width:720px;margin:2rem auto;line-height:1.5;color:#1a1a1a}h1{font-size:1.5rem}.warning{background:#fff3cd;border:1px solid #ffc107;padding:.75rem;border-radius:6px}section{margin:1.25rem 0;padding:1rem;border:1px solid #ddd;border-radius:8px}footer{margin-top:2rem;font-size:.85rem;color:#555}dl{display:grid;grid-template-columns:auto 1fr;gap:.25rem 1rem}</style></head><body><h1>${escapeHtml(view.title)}</h1>${warning}<dl><dt>Business</dt><dd>${escapeHtml(view.businessName)}</dd><dt>Agency</dt><dd>${escapeHtml(view.agencyDisplayName)}</dd><dt>Proof reference</dt><dd>${escapeHtml(view.proofReferenceLabel)}</dd><dt>Status</dt><dd>${escapeHtml(view.aggregateStatusLabel)}</dd><dt>Issued</dt><dd>${escapeHtml(view.issuedAt)}</dd></dl><p>${escapeHtml(view.headline)}</p>${sectionsHtml}<footer><ul>${disclaimers}</ul></footer></body></html>`;
}
function escapeHtml(value) {
    return value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}
export class ClientSafeLaunchCertificateUnsafeError extends Error {
    constructor(message) {
        super(message);
        this.name = "ClientSafeLaunchCertificateUnsafeError";
    }
}
//# sourceMappingURL=sanitize-client-safe-launch-certificate.js.map